import React from "react";
import { Link } from "react-router-dom";
import {ArrowLeft, Shield, Globe, FileText} from 'lucide-react';
import { motion } from "framer-motion";
import BookingModal from "../../components/BookingModal";

const YellowFeverVaccineRequirements: React.FC = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      <section className="relative py-20 bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/resources" className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:gap-3 transition-all mb-8">
            <ArrowLeft className="w-5 h-5" />
            Back to Resources
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-cyan-500 text-white px-3 py-1 rounded-full text-sm font-bold">Requirements</div>
              <span className="text-sm text-gray-500 dark:text-gray-400">7 min read</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white">
              Yellow Fever Vaccine Requirements by Country 2024
            </h1>

            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Comprehensive list of countries requiring yellow fever vaccination certificates. Find out if your destination requires proof of yellow fever vaccination.
            </p>
          </motion.div>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <img src="https://static.lumi.new/c3/c35b33fc26af799b9204702f07e329c9.jpeg" alt="Yellow fever vaccination" className="w-full h-96 object-cover rounded-2xl mb-12" />

          <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Understanding Yellow Fever Requirements</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            Yellow fever is a serious viral disease transmitted by mosquitoes in tropical regions of Africa and South America. Many countries require proof of yellow fever vaccination for entry, and failure to provide this documentation can result in denial of entry or mandatory quarantine.
          </p>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-6 mb-8">
            <h3 className="font-bold text-yellow-900 dark:text-yellow-300 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              The Yellow Card (International Certificate of Vaccination)
            </h3>
            <p className="text-yellow-800 dark:text-yellow-200">
              After receiving the yellow fever vaccine, you'll receive an official "yellow card" - the International Certificate of Vaccination or Prophylaxis (ICVP). This document is valid for life and must be presented at border crossings.
            </p>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
            <Shield className="w-6 h-6 text-cyan-500" />
            Who Needs Yellow Fever Vaccination?
          </h3>

          <p className="text-gray-700 dark:text-gray-300 mb-4">Countries have different yellow fever requirements based on two main scenarios:</p>

          <div className="space-y-6 mb-8">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">1. Countries with Yellow Fever Risk</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                Countries where yellow fever is present require vaccination for all travelers over a certain age (usually 9 months to 1 year).
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400"><strong>Example:</strong> Brazil, Kenya, Ghana, Peru, Uganda</p>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">2. Countries Requiring Proof from Travelers</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                Countries without yellow fever but requiring proof if you're arriving from (or transiting through) a yellow fever endemic country.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400"><strong>Example:</strong> India, Thailand, Singapore, Australia</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
            <Globe className="w-6 h-6 text-cyan-500" />
            Africa - Countries with Yellow Fever Requirements
          </h3>

          <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-6 mb-6">
            <h4 className="font-bold mb-3 text-gray-900 dark:text-white">Vaccination Required (Endemic Countries)</h4>
            <div className="grid grid-cols-2 gap-2 text-gray-700 dark:text-gray-300 text-sm">
              <ul className="list-disc pl-5 space-y-1">
                <li>Angola</li>
                <li>Benin</li>
                <li>Burkina Faso</li>
                <li>Burundi</li>
                <li>Cameroon</li>
                <li>Central African Republic</li>
                <li>Chad</li>
                <li>Congo (Brazzaville)</li>
                <li>Congo (Democratic Republic)</li>
                <li>Côte d'Ivoire</li>
              </ul>
              <ul className="list-disc pl-5 space-y-1">
                <li>Equatorial Guinea</li>
                <li>Ethiopia</li>
                <li>Gabon</li>
                <li>Gambia</li>
                <li>Ghana</li>
                <li>Guinea</li>
                <li>Guinea-Bissau</li>
                <li>Kenya</li>
                <li>Liberia</li>
                <li>Mali</li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-2 text-gray-700 dark:text-gray-300 text-sm mt-2">
              <ul className="list-disc pl-5 space-y-1">
                <li>Mauritania</li>
                <li>Niger</li>
                <li>Nigeria</li>
                <li>Rwanda</li>
                <li>Senegal</li>
              </ul>
              <ul className="list-disc pl-5 space-y-1">
                <li>Sierra Leone</li>
                <li>South Sudan</li>
                <li>Sudan</li>
                <li>Togo</li>
                <li>Uganda</li>
              </ul>
            </div>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">South America - Yellow Fever Endemic Countries</h3>

          <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-6 mb-6">
            <h4 className="font-bold mb-3 text-gray-900 dark:text-white">Countries with Yellow Fever Risk</h4>
            <ul className="grid grid-cols-2 gap-2 text-gray-700 dark:text-gray-300 text-sm list-disc pl-5">
              <li>Argentina (northern regions)</li>
              <li>Bolivia</li>
              <li>Brazil (most areas)</li>
              <li>Colombia</li>
              <li>Ecuador</li>
              <li>French Guiana</li>
              <li>Guyana</li>
              <li>Panama (eastern regions)</li>
              <li>Paraguay</li>
              <li>Peru</li>
              <li>Suriname</li>
              <li>Trinidad and Tobago</li>
              <li>Venezuela</li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Important Transit Considerations</h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Many countries require yellow fever vaccination if you're transiting through an endemic country, even if you don't leave the airport. Common transit scenarios include:
          </p>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 mb-6">
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li><strong>✈️ Connecting flights through African hubs:</strong> Addis Ababa, Nairobi, Johannesburg</li>
              <li><strong>✈️ South American connections:</strong> São Paulo, Lima, Bogotá</li>
              <li><strong>✈️ Even short layovers count:</strong> Some countries consider any transit, regardless of duration</li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Vaccine Information</h3>

          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center shrink-0 font-bold">1</div>
              <div>
                <h4 className="font-bold text-gray-900 dark:text-white mb-1">Timing</h4>
                <p className="text-gray-700 dark:text-gray-300">Must be given at least 10 days before entry to endemic countries</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center shrink-0 font-bold">2</div>
              <div>
                <h4 className="font-bold text-gray-900 dark:text-white mb-1">Validity</h4>
                <p className="text-gray-700 dark:text-gray-300">One dose provides lifelong protection (as of 2016, WHO updated requirement)</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center shrink-0 font-bold">3</div>
              <div>
                <h4 className="font-bold text-gray-900 dark:text-white mb-1">Cost</h4>
                <p className="text-gray-700 dark:text-gray-300">Approximately $150-200 in Canada</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center shrink-0 font-bold">4</div>
              <div>
                <h4 className="font-bold text-gray-900 dark:text-white mb-1">Designated Centers</h4>
                <p className="text-gray-700 dark:text-gray-300">Only designated yellow fever vaccination centers can administer and issue certificates</p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-6 mb-6">
            <h3 className="font-bold text-red-900 dark:text-red-300 mb-2">Contraindications - Who Should Not Get Yellow Fever Vaccine</h3>
            <ul className="list-disc pl-6 text-red-800 dark:text-red-200 space-y-1">
              <li>Infants under 9 months</li>
              <li>Pregnant or breastfeeding women (unless travel is unavoidable)</li>
              <li>People with severe allergies to vaccine components</li>
              <li>People with weakened immune systems (HIV/AIDS, cancer treatment)</li>
              <li>People with thymus disorders</li>
              <li>Adults over 60 (higher risk of side effects - discuss with doctor)</li>
            </ul>
            <p className="text-red-800 dark:text-red-200 mt-3 font-bold">
              If you cannot receive the vaccine for medical reasons, you may be able to obtain a medical waiver letter, though some countries may not accept it.
            </p>
          </div>

          <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Key Takeaways</h3>
          <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-6">
            <ul className="space-y-3 text-gray-700 dark:text-gray-300">
              <li>✅ Check yellow fever requirements for EVERY country in your itinerary, including transit points</li>
              <li>✅ Get vaccinated at least 10 days before travel</li>
              <li>✅ Keep your yellow card safe - you'll need it at borders and may need it for future trips</li>
              <li>✅ Ensure your vaccine is administered at a designated yellow fever center</li>
              <li>✅ Consider trip routing to avoid unnecessary yellow fever requirements</li>
            </ul>
          </div>
        </div>
      </article>

      <section className="py-16 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Traveling to a Yellow Fever Zone?</h2>
          <p className="text-xl text-white/90 mb-8">Get expert guidance on yellow fever requirements and vaccination for your specific itinerary.</p>
          <button onClick={() => setIsBookingModalOpen(true)} className="px-8 py-4 bg-white text-cyan-600 font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
            Book Virtual Consult
          </button>
        </div>
      </section>

      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
    </div>
  );
};

export default YellowFeverVaccineRequirements;